import React, { lazy, Suspense, useContext, useEffect, useState } from 'react';
import { CircularProgress, Container, createTheme, CssBaseline, Paper, ThemeProvider, Typography } from '@material-ui/core';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import OrderScreen from './screens/OrderScreen.jsx';
import { Store } from './store.jsx';
import InactivityHandler from './components/InactivityHandler.jsx';
import MaintenanceScreen from './components/MaintenanceScreen.jsx';
import { useMaintenanceMode } from './hooks/useMaintenanceMode.js';
import { useBusinessHours } from './hooks/useBusinessHours.js';
import { withCleanProps } from './utils/cleanProps.jsx';
import request from './api.js';
import { MATRIZID } from './constants.js';
import { getRestauranteIdSync, ensureRestauranteLoaded } from './services/selectedRestaurante';
import SelecionarRestaurante from './screens/SelecionarRestaurante.jsx';
import ClosedScreen from './components/ClosedScreen.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import { useGlobalErrorHandler } from './hooks/useGlobalErrorHandler.js';

const AdminScreen = lazy(() => import('./screens/AdminScreen.jsx'));
const ChooseOrderType = lazy(() => import('./screens/ChooseOrderType.jsx'));
const CompletedOrderScreen = lazy(() => import('./screens/CompletedOrderScreen.jsx'));
const Home = lazy(() => import('./screens/Home.jsx'));
const PaymentScreen = lazy(() => import('./screens/PaymentScreen.jsx'));
const QueueScreen = lazy(() => import('./screens/QueueScreen.jsx'));
const ReviewScreen = lazy(() => import('./screens/ReviewScreen.jsx'));
const SelectPaymentScreen = lazy(() => import('./screens/SelectPaymentScreen.jsx'));
const WhatsAppScreen = lazy(() => import('./screens/WhatsAppScreen.jsx'));
const TotemConfigScreen = lazy(() => import('./screens/TotemConfigScreen.jsx'));

// Versões limpas dos componentes Material-UI
const CleanThemeProvider = withCleanProps(ThemeProvider);
const CleanCssBaseline = withCleanProps(CssBaseline);
const CleanContainer = withCleanProps(Container);
const CleanPaper = withCleanProps(Paper);
const CleanCircularProgress = withCleanProps(CircularProgress);
const CleanTypography = withCleanProps(Typography);

const theme = createTheme({
  typography: {
    fontFamily: '"Montserrat", sans-serif',
    h1: {
      fontSize: '3.5rem',
      fontWeight: 'bold',
      fontFamily: '"Montserrat", sans-serif',
    },
    h2: {
      fontSize: '2.2rem',
      fontWeight: 'bold',
      color: 'black',
      fontFamily: '"Montserrat", sans-serif',
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: 'white',
      fontFamily: '"Montserrat", sans-serif',
    },
    h4: {
      fontSize: '1.8rem',
      fontFamily: '"Montserrat", sans-serif',
    },
    body1: {
      fontSize: '1.4rem',
      fontFamily: '"Montserrat", sans-serif',
    },
    body2: {
      fontSize: '1.2rem',
      fontFamily: '"Montserrat", sans-serif',
    },
    button: {
      fontSize: '1.6rem',
      fontWeight: 'bold',
      fontFamily: '"Montserrat", sans-serif',
    },
  },
  palette: {
    primary: {
      main: '#ff1744'
    },
    secondary: {
      main: '#118e16',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f5f5',
    }
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        body: {
          fontFamily: '"Montserrat", sans-serif',
          touchAction: 'manipulation', // Improves touch response
        },
      },
    },
    MuiButton: {
      root: {
        borderRadius: 12,
        padding: '12px 24px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
        transition: 'all 0.3s ease',
      },
      contained: {
        '&:hover': {
          transform: 'scale(1.02)',
        },
      },
    },
    MuiCard: {
      root: {
        borderRadius: 16,
        boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
        transition: 'transform 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
        },
      },
    },
    MuiTextField: {
      root: {
        '& .MuiInputBase-input': {
          fontSize: '1.6rem', // Larger text for inputs
        },
        '& .MuiInputLabel-root': {
          fontSize: '1.4rem', // Larger labels
        },
      },
    },
    MuiCircularProgress: {
      root: {
        width: '60px !important',
        height: '60px !important',
      },
    },
  },
})

function App() {
  const { state } = useContext(Store);
  const { 
    isMaintenanceMode, 
    healthData, 
    lastCheck
  } = useMaintenanceMode();

  const { isOpen, proximaAbertura } = useBusinessHours();

  const [restaurante, setRestaurante] = useState({});

  // Usar hook para capturar erros globais
  useGlobalErrorHandler();

  useEffect(() => {
    const handlePopState = () => {
      // Page navigation tracking removed
    };
    
    window.addEventListener('popstate', handlePopState);
    
    const loadRestaurante = async () => {
      let rest = null;

      rest = localStorage.getItem('restaurante') ? JSON.parse(localStorage.getItem('restaurante')) : null;

      if (!rest) {
        rest = await ensureRestauranteLoaded();
      }

      setRestaurante(rest || {});

      if (rest) localStorage.setItem('restaurante', JSON.stringify(rest));
    };

    loadRestaurante();
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Se estiver em modo manutenção, exibe tela de manutenção
  if (isMaintenanceMode) {
    return (
      <CleanThemeProvider theme={theme}>
        <CleanCssBaseline />
        <MaintenanceScreen
          healthData={healthData}
          lastCheck={lastCheck}
        />
      </CleanThemeProvider>
    );
  }

  // Se o restaurante estiver fechado, exibe tela de fechamento
  if (!isOpen) {
    return (
      <CleanThemeProvider theme={theme}>
        <CleanCssBaseline />
        <ClosedScreen proximaAbertura={proximaAbertura} />
      </CleanThemeProvider>
    );
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <CleanThemeProvider theme={theme}>
          <CleanCssBaseline />
          <CleanContainer disableGutters>
            <CleanPaper elevation={0}>
              <InactivityHandler />
              <Suspense fallback={
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100vh',
                  flexDirection: 'column',
                  background: restaurante.corPrimaria || '#fa5b6d'
                }}>
                  <CleanCircularProgress size={80} style={{ color: 'white', marginBottom: 20 }} />
                  <CleanTypography variant="h3">Carregando...</CleanTypography>
                </div>
              }>
                <TotemConfigScreen />
                <Switch>
                  <Route path="/selecionar-restaurante" component={SelecionarRestaurante} exact={true} />
                  <Route path="/" component={Home} exact={true} />
                  <Route path="/choose-order-type" component={ChooseOrderType} exact={true} />
                  <Route path="/order" component={OrderScreen} exact={true} />
                  <Route path="/review" component={ReviewScreen} exact={true} />
                  <Route path="/whatsapp" component={WhatsAppScreen} exact={true} />
                  <Route path="/select-payment" component={SelectPaymentScreen} exact={true} />
                  <Route path="/payment" component={PaymentScreen} exact={true} />
                  <Route path="/complete" component={CompletedOrderScreen} exact={true} />
                  <Route path="/admin" component={AdminScreen} exact={true} />
                  <Route path="/queue" component={QueueScreen} exact={true} />
                  <Route path="/test-closed" render={() => <ClosedScreen proximaAbertura="Terça-feira às 08:00" />} exact={true} />
                </Switch>
              </Suspense>
            </CleanPaper>
          </CleanContainer>
        </CleanThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
